import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "@/hooks/useAuth";

type Props = {
  open: boolean;
  initialEmail?: string;
  onClose: () => void;
};

export default function ForgotPasswordModal({ open, initialEmail = "", onClose }: Props) {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(initialEmail);
      setError(null);
      setSent(false);
      setLoading(false);
    }
  }, [open, initialEmail]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError("Enter the email tied to your account.");
      return;
    }
    setLoading(true);
    const { error: err } = await requestPasswordReset(email.trim());
    setLoading(false);
    if (err) {
      setError(err);
      return;
    }
    setSent(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-ink-900/55 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      <div className="relative w-full max-w-md bg-white rounded-xl border border-ink-100 overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-ink-100">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-md bg-brand-50 text-brand-600">
              <i className="ri-lock-password-line text-lg"></i>
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-ink-900 leading-tight">
                Reset your password
              </h3>
              <p className="text-xs text-ink-500 mt-0.5">
                We&apos;ll email you a secure link to set a new one.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-50 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {sent ? (
          <div className="px-5 py-6">
            <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-sm text-emerald-800">
              <i className="ri-mail-check-line text-base mt-0.5 shrink-0"></i>
              <div>
                <div className="font-semibold">Check your inbox</div>
                <div className="mt-0.5 text-emerald-700">
                  If an account exists for <span className="font-semibold">{email}</span>, a reset
                  link is on its way. The link expires in about an hour.
                </div>
              </div>
            </div>
            <p className="text-xs text-ink-500 mt-3">
              Didn&apos;t get it? Check your spam folder, then try again.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSent(false)}
                className="px-3.5 py-2 rounded-md text-sm font-semibold text-ink-700 hover:bg-ink-50 cursor-pointer whitespace-nowrap"
              >
                Send to a different email
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-2 rounded-md text-sm font-semibold bg-ink-900 text-white hover:bg-ink-800 cursor-pointer whitespace-nowrap"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-5 py-5 space-y-4">
            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-700 flex items-start gap-2">
                <i className="ri-error-warning-line text-base mt-0.5 shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            <div>
              <label
                htmlFor="reset-email"
                className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider"
              >
                Email
              </label>
              <div className="relative">
                <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
                  <i className="ri-mail-line text-sm"></i>
                </span>
                <input
                  id="reset-email"
                  type="email"
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@haulmark.co"
                  className="w-full text-sm pl-10 pr-3 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-2 rounded-md text-sm font-semibold text-ink-700 hover:bg-ink-50 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending…
                  </>
                ) : (
                  <>
                    Send reset link
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}